import React, {
  KeyboardEventHandler,
  MutableRefObject,
  SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import Note from '@repo/utils/models/Note';
import { KeyboardShortcut } from '@repo/components/tooltips';
import { CheckIcon, LoaderIcon, LockIcon, ReminderAlarmClockIcon, XIcon } from '@repo/icons';
import styles from './NoteEditor.module.scss';
import Occasion from '@repo/utils/helpers/Occasion';
import { useOutsideEventHandler } from '@repo/hooks';
import { useTheme } from '@repo/themes';
import { metaSymbol } from '@repo/utils/helpers/keyboardHandler';
import {
  Tooltip,
  Overlay,
  DeleteReminderTooltip,
  EditReminderTooltip,
  SetReminderTooltip,
} from '@repo/components/tooltips';
import TextEditor from '@repo/components/text-editor';
import { DateTimePicker } from '@repo/components/forms';

function ReminderEditorTrigger({ note }: { note: Note }) {
  const date = note.reminder?.date;
  const dateDisplayFormat = date?.isThisYear ? 'EEE, MMM dd' : 'EEE, MMM dd yyyy';

  if (!date) {
    return (
      <SetReminderTooltip>
        <div className={cn(styles.addReminderTrigger, 'add-reminder-trigger')}>
          <ReminderAlarmClockIcon />
        </div>
      </SetReminderTooltip>
    );
  }

  return (
    <EditReminderTooltip>
      <div className={cn(styles.reminderTrigger, !date.isPast && styles.upcoming)}>
        <ReminderAlarmClockIcon colorized={!date.isPast} />
        {date.format(dateDisplayFormat)} {date.format('h:mm aa')}
      </div>
    </EditReminderTooltip>
  );
}

function ReminderEditor({
  note,
  updateReminder,
  removeReminder,
  defaultIsOpen,
}: {
  note: Note;
  updateReminder: (date: Occasion) => void;
  removeReminder: () => void;
  defaultIsOpen?: boolean;
}) {
  return (
    <div
      className={cn(
        styles.reminderEditor,
        note.reminder?.date?.isPast && styles.reminderInPast,
        !note.reminder?.date && styles.noReminderSet,
      )}
    >
      <DateTimePicker
        dateTime={note.reminder?.date}
        setDateTime={updateReminder}
        trigger={<ReminderEditorTrigger note={note} />}
        disabled={note.reminder?.date?.isPast}
        defaultIsOpen={defaultIsOpen}
        menuSide="top"
      />
      {note.reminder?.date && (
        <DeleteReminderTooltip>
          <button className={styles.deleteReminderBtn} onClick={removeReminder}>
            <XIcon width={11} height={11} />
          </button>
        </DeleteReminderTooltip>
      )}
    </div>
  );
}

type NoteEditorProps = {
  note: Note;
  inputRef?: MutableRefObject<HTMLInputElement | undefined>;
  onNoteContentChange: (content: string) => void;
  onNoteReminderChange: (date: Occasion | null) => void;
  onNoteSave?: (e?: SyntheticEvent) => void;
  onBlur?: (e: SyntheticEvent) => void;
  onNoteDismiss?: () => void;
  showDismissBtn?: boolean;
  showSaveBtn?: boolean;
  saveDisabled?: boolean;
  noteSaving?: boolean;
  noteSavedState?: '' | 'error' | 'saved';
  details?: boolean;
  removeable?: boolean;
  wrapperClassName?: string;
  className?: string;
  footerClassName?: string;
  large?: boolean;
  ignoreOutsideClicks?: boolean;
  header?: React.ReactNode;
  defaultReminderEditorIsOpen?: boolean;
};

export function NoteEditor(props: NoteEditorProps) {
  const [followupOccasion, setFollowupOccasion] = useState(props.note.reminder?.date);
  const [followupEntityInteracted, setFollowupEntityInteracted] = useState(false);
  const [theme] = useTheme();

  const formRef = useRef(null);
  const saveDisabled =
    props.saveDisabled !== undefined
      ? props.saveDisabled
      : props.note.content.length == 0 || props.note.isFetching;

  useOutsideEventHandler(formRef, (e) => {
    if (!props.ignoreOutsideClicks && props.onBlur) {
      props.onBlur(e);
    }
  });

  useEffect(() => {
    props.onNoteReminderChange(followupOccasion);
    if (!followupOccasion && followupEntityInteracted) {
      setFollowupEntityInteracted(false);
    }
  }, [followupOccasion]);

  const handleKeyDown: KeyboardEventHandler = (e) => {
    if (e.keyCode == 27 && props.onBlur) {
      props.onBlur(e);
    }
  };

  const handleFormSubmit = (e) => {
    if (props.onNoteSave) {
      props.onNoteSave();
    }
    e.preventDefault();
  };

  return (
    <div ref={formRef} onKeyDown={handleKeyDown} className={cn(props.wrapperClassName)}>
      <TextEditor
        className={cn(styles.note, props.className, props.large && styles.noteLarge)}
        content={props.note.content}
        onContentChange={props.onNoteContentChange}
        inputRef={props.inputRef}
        followupOccasion={followupOccasion}
        setFollowupOccasion={setFollowupOccasion}
        setFollowupEntityInteracted={setFollowupEntityInteracted}
        header={props.header}
        readOnly={props.note.isReadOnly}
        onSave={props.onNoteSave}
      />
      <div className={cn(styles.footer, props.footerClassName)}>
        <div className={styles.details}>
          {!props.note.isReadOnly && (
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <ReminderEditor
                note={props.note}
                updateReminder={props.onNoteReminderChange}
                removeReminder={() => props.onNoteReminderChange(null)}
                defaultIsOpen={props.defaultReminderEditorIsOpen}
              />
            </div>
          )}
        </div>
        <div className={styles.actions}>
          {props.showDismissBtn && (
            <button
              onClick={props.onNoteDismiss ? props.onNoteDismiss : () => {}}
              className={`btn-none ${styles.remove}`}
              type="button"
            >
              <XIcon width={15} height={15} color="primary" strokeWidth="2.3" />
            </button>
          )}
          {props.noteSavedState == 'error' && (
            <span className={cn(styles.statusMsg, styles.error)}>Please try again</span>
          )}
          {props.noteSavedState == 'saved' && <span className={styles.statusMsg}>Saved</span>}
          {!props.noteSaving && !props.note.isReadOnly && (
            <KeyboardShortcut keys={[metaSymbol, 'enter']} placement="left">
              <button
                disabled={saveDisabled || !props.showSaveBtn || props.noteSaving}
                className={`btn-none ${styles.submit}`}
                onClick={handleFormSubmit}
              >
                <CheckIcon width={15} height={15} color="primary" strokeWidth="2.3" />
              </button>
            </KeyboardShortcut>
          )}
          {props.noteSaving && <LoaderIcon width="15" />}
          {props.note.isReadOnly && (
            <Tooltip placement="right" overlay={<Overlay>Read-only</Overlay>}>
              <LockIcon
                width="16"
                height="18"
                viewBox="7.5 7 9 10"
                customColor={theme === 'light' ? '#bbb' : '#999'}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
